import { Thermometer, TrendingDown, Snowflake, Wrench, Phone } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { CTAButton } from "@/components/CTAButton";
import {
  Section,
  SectionHeading,
  Breadcrumb,
  InfoGrid,
  CTABanner,
} from "@/components/Sections";
import { BUSINESS } from "@/lib/site";

const SIGNS = [
  { icon: "Thermometer", title: "AC Running But Not Cooling or Heating", text: "The unit operates but the room does not reach the expected cooling or heating temperature." },
  { icon: "TrendingDown", title: "Reduced Cooling or Heating Performance", text: "Cooling or heating performance has gradually become weaker than normal." },
  { icon: "Snowflake", title: "Ice / Freezing", text: "Ice may appear on pipework or parts of the system under certain operating conditions." },
  { icon: "Wrench", title: "Previous Installation or Connection Issues", text: "Problems around previous pipework or refrigerant connections may contribute to refrigerant loss." },
];
const ICONS = { Thermometer, TrendingDown, Snowflake, Wrench };

const STEPS = [
  { title: "System Check", text: "Check cooling and heating performance and overall system operation." },
  { title: "Connection Inspection", text: "Inspect accessible refrigerant connections where appropriate." },
  { title: "Pressure & Operating Checks", text: "Carry out relevant operating and refrigerant checks." },
  { title: "Refrigerant Refill", text: "Add the correct refrigerant when required." },
  { title: "Final Performance Check", text: "Check system operation and cooling or heating performance after service." },
];

export default function GasRefill() {
  return (
    <>
      <SEO
        title="AC Gas Refill Paphos | NorthWork Cyprus"
        description="Professional refrigerant-related inspection and AC gas refill in Paphos for systems with reduced cooling or heating performance. NorthWork Cyprus."
        path="/ac-gas-refill-paphos"
        schema={{ "@type": "Service", serviceType: "AC Gas Refill", areaServed: "Paphos" }}
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "AC Gas Refill" }]} />

      {/* Hero */}
      <section data-testid="page-hero" className="relative isolate overflow-hidden border-b border-border">
        {/* Full-width background image — separate desktop vs mobile crop */}
        <img
          src="/images/gas-refill-hero-bg.webp"
          alt="Outdoor AC unit connected to manifold gauges, hoses and a refrigerant cylinder for a gas refill by NorthWork Cyprus in Paphos"
          loading="eager"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[68%_52%] md:object-[center_50%]"
        />
        {/* Dark overlay: darker on the left/text side, right/equipment kept clearer; stronger overall on mobile */}
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0e14]/98 via-[#0b0e14]/82 to-[#0b0e14]/22 md:from-[#0b0e14]/97 md:via-[#0b0e14]/74 md:to-[#0b0e14]/28"
          aria-hidden="true"
        />
        <div className="container-x grid items-center gap-10 py-8 md:py-12 lg:grid-cols-12 lg:gap-14 lg:py-20">
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <h1 className="font-heading text-[2.6rem] font-medium leading-[1.04] tracking-tight text-white md:text-5xl md:leading-[1.04] lg:text-[4rem]">
                AC Gas Refill<br /> in Paphos
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-4 max-w-xl md:mt-6">
              <p className="text-base leading-relaxed text-white/85 lg:text-lg">
                Professional refrigerant checks and refill service for air-conditioning systems with
                reduced cooling or heating performance.
              </p>
              <p className="mt-3 hidden max-w-lg text-sm leading-relaxed text-white/70 md:block">
                We inspect the system and refrigerant condition first, then carry out a refill where
                appropriate.
              </p>
            </Reveal>
            <Reveal delay={0.15} variant="up" className="mt-6 flex flex-col gap-3 sm:flex-row md:mt-8">
              <CTAButton to="/contact" testid="hero-primary-cta">
                Book a Gas Check
              </CTAButton>
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid="hero-call-cta" className="hidden md:inline-flex">
                {BUSINESS.phoneDisplay}
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <Section className="py-24 lg:py-32">
        <div className="container-x grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading title="Does Your AC Need Refrigerant?" />
          </div>
          <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-charcoal-muted lg:col-span-7 lg:text-lg">
            <p>
              Air-conditioning refrigerant operates inside a sealed system. If the refrigerant level
              is low, cooling or heating performance can be affected and the system should be checked
              to understand why.
            </p>
            <p>
              NorthWork Cyprus checks the system and accessible connections before carrying out a
              refrigerant refill when required.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Possible signs */}
      <Section className="border-t border-border bg-surface py-24 lg:py-32">
        <div className="container-x">
          <SectionHeading title="Possible Signs" />
          <div className="mt-14">
            <InfoGrid items={SIGNS} columns={4} iconMap={ICONS} testid="signs-grid" />
          </div>
          <p className="mt-6 max-w-2xl text-[13px] leading-relaxed text-charcoal-muted">
            These symptoms can also be caused by other AC faults, which is why inspection comes first.
          </p>
        </div>
      </Section>

      {/* Process — vertical technical list */}
      <Section className="py-24 lg:py-32">
        <div className="container-x">
          <SectionHeading title="Gas Refill & System Check" />
          <div className="mt-14 border-t border-border">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div className="grid grid-cols-12 items-baseline gap-4 border-b border-border py-7 transition-colors hover:bg-surface">
                  <span className="col-span-2 font-mono-t text-sm text-brand sm:col-span-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-10 font-heading text-xl font-semibold text-charcoal sm:col-span-4">
                    {s.title}
                  </h3>
                  <p className="col-span-12 text-[15px] leading-relaxed text-charcoal-muted sm:col-span-7">
                    {s.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 max-w-2xl text-[13px] leading-relaxed text-charcoal-muted">
            If a significant fault or leak is identified, we explain the available options and advise
            on the appropriate next step.
          </p>
        </div>
      </Section>

      <CTABanner
        title="Poor Cooling or Heating Performance?"
        text="Arrange an inspection with NorthWork Cyprus to determine whether the problem is refrigerant-related or caused by another AC fault."
        primaryLabel="Arrange Inspection"
        primaryTo="/contact"
        image="/images/gas-refill-cta.webp"
        imageClass="object-[82%_center] lg:object-center"
        paddingClass="py-16 lg:py-24"
        overlayStyle="linear-gradient(to right, rgba(6,13,26,0.80) 0%, rgba(6,13,26,0.62) 30%, rgba(6,13,26,0.46) 55%, rgba(6,13,26,0.34) 80%, rgba(6,13,26,0.30) 100%)"
      />
    </>
  );
}
